import {
  PreScreeningQuestion,
  QuestionType,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  GLP1_MEDICATIONS,
  DPP4_MEDICATIONS,
  IMMUNOSUPPRESSANT_MEDICATIONS,
} from "../../utils/attributeMapping/attributeConstants";
import { vikingT2dScreeningQuestions } from "./VikingT2d";

/**
 * AI-powered variant of Viking T2D Study prescreener
 * Consolidates all medication questions into a single AI-powered entry
 */
export const vikingT2dScreeningQuestionsAI: PreScreeningQuestion[] = [
  // Keep initial diabetes screening questions
  vikingT2dScreeningQuestions.find(q => q.id === "diabetesType")!,
  vikingT2dScreeningQuestions.find(q => q.id === "hba1cLevel")!,
  vikingT2dScreeningQuestions.find(q => q.id === "height")!,
  vikingT2dScreeningQuestions.find(q => q.id === "weight")!,
  
  // Single AI medication entry replacing questions 5-8 (glp1History, dpp4History, stimulantOrWeightLossHistory, corticosteroidHistory)
  {
    id: "medicationAI",
    type: QuestionType.AI_MEDICATION_ENTRY,
    question: "Please list all medications you are currently taking or have taken recently",
    helpText: "Include ALL medications: prescription drugs, over-the-counter medicines, supplements, vitamins, and any experimental treatments. Please also mention if you've recently stopped any medications.",
    required: true,
    allowBack: true,
    aiConfig: {
      studyContext: "viking-t2d",
      experimentName: "viking_t2d_ai_medications",
      medicationCategories: [
        {
          category: "glp1",
          medications: [
            // Semaglutide
            "Ozempic", "Wegovy", "Semaglutide", "Rybelsus",
            // Tirzepatide (dual GLP-1/GIP)
            "Mounjaro", "Zepbound", "Tirzepatide",
            // Liraglutide
            "Victoza", "Saxenda", "Liraglutide",
            // Dulaglutide
            "Trulicity", "Dulaglutide",
            // Exenatide
            "Byetta", "Bydureon", "Bydureon BCise", "Exenatide",
            // Lixisenatide
            "Adlyxin", "Lixisenatide", "Lyxumia",
            // Albiglutide
            "Tanzeum", "Albiglutide", "Eperzan",
            // Semaglutide/liraglutide combinations
            "Xultophy", "Soliqua",
            // Generic terms
            "GLP-1", "GLP1", "GLP-1 agonist", "GLP1 agonist",
            "GIP agonist", "dual agonist"
          ],
          disqualifyIfCurrent: true,
          disqualifyWithin: "6 months",
        },
        {
          category: "dpp4_inhibitors",
          medications: [
            // Sitagliptin
            "Januvia", "Sitagliptin",
            "Janumet", "Janumet XR", "Sitagliptin with Metformin",
            "Juvisync", "Simvastatin with Sitagliptin",
            "Steglujan", "Ertugliflozin with Sitagliptin",
            // Alogliptin
            "Nesina", "Alogliptin",
            "Kazano", "Alogliptin with Metformin",
            "Oseni", "Alogliptin with Pioglitazone",
            // Saxagliptin
            "Onglyza", "Saxagliptin",
            "Kombiglyze", "Kombiglyze XR", "Metformin with Saxagliptin",
            "Qtern", "Qternmet", "Qternmet XR", "Dapagliflozin with Saxagliptin",
            // Linagliptin
            "Tradjenta", "Linagliptin",
            "Jentadueto", "Jentadueto XR", "Linagliptin with Metformin",
            "Glyxambi", "Linagliptin with Empagliflozin",
            "Trijardy", "Trijardy XR", "Empagliflozin/Linagliptin/Metformin",
            // Vildagliptin (not US but may be mentioned)
            "Galvus", "Vildagliptin",
            // Generic terms
            "DPP-4", "DPP4", "DPP-4 inhibitor", "DPP4 inhibitor",
            "Dipeptidyl peptidase 4 inhibitor"
          ],
          disqualifyIfCurrent: true,
        },
        {
          category: "insulin",
          medications: [
            // Any form of insulin
            "Insulin", 
            // Long-acting
            "Lantus", "Levemir", "Tresiba", "Toujeo", "Basaglar", "Semglee",
            "Insulin glargine", "Insulin detemir", "Insulin degludec",
            // Rapid-acting
            "Humalog", "Novolog", "Apidra", "Fiasp", "Admelog", "Lyumjev",
            "Insulin lispro", "Insulin aspart", "Insulin glulisine",
            // Short-acting
            "Regular insulin", "Humulin R", "Novolin R",
            // Intermediate-acting
            "NPH insulin", "Humulin N", "Novolin N",
            // Premixed
            "Humulin", "Novolin", "Humalog Mix", "Novolog Mix",
            // Inhaled
            "Afrezza", "Inhaled insulin",
            // Combinations
            "Xultophy", "Soliqua", "iGlarLixi",
            // Generic terms
            "Insulin injection", "Insulin pen", "Insulin pump"
          ],
          disqualifyIfCurrent: true,
        },
        {
          category: "amylin_analogues",
          medications: [
            "Symlin", "Pramlintide", "Amylin", "Amylin analog", "Amylin agonist"
          ],
          disqualifyIfCurrent: true,
          disqualifyWithin: "6 months",
        },
        {
          category: "stimulants",
          medications: [
            // Amphetamines - specifically called out in protocol
            "Adderall", "Adderall XR", "Adderall IR",
            "Amphetamine", "Dextroamphetamine", "Amphetamine salts",
            "Dexedrine", "Dextroamphetamine sulfate", "Dexedrine Spansule",
            "Vyvanse", "Lisdexamfetamine", "Elvanse",
            "Mydayis", "Evekeo", "Dynavel", "Adzenys", "Dyanavel",
            // Methylphenidate products
            "Ritalin", "Ritalin LA", "Ritalin SR",
            "Concerta", "Methylphenidate", "Methylphenidate ER",
            "Focalin", "Focalin XR", "Dexmethylphenidate",
            "Metadate", "Methylin", "Quillivant", "Cotempla", "Jornay",
            "Daytrana", "Methylphenidate patch",
            // Other ADHD medications
            "Strattera", "Atomoxetine", // Non-stimulant but often grouped
            "Desoxyn", "Methamphetamine", // Rarely prescribed
            // Wakefulness agents
            "Modafinil", "Provigil", "Armodafinil", "Nuvigil",
            // Generic terms
            "ADHD medication", "ADD medication", "Stimulant"
          ],
          disqualifyIfCurrent: true,
          disqualifyWithin: "12 weeks", // Protocol says 12 weeks for Adderall
        },
        {
          category: "weight_loss",
          medications: [
            // Prescription weight loss
            "Phentermine", "Adipex", "Adipex-P", "Lomaira", "Suprenza",
            "Orlistat", "Xenical", "Alli",
            "Contrave", "Naltrexone/Bupropion", "Mysimba",
            "Qsymia", "Phentermine/Topiramate",
            "Imcivree", "Setmelanotide",
            "Plenity", "Gelesis100",
            "Saxenda", // Also a GLP-1 but specifically for weight loss
            "Wegovy", // Also a GLP-1 but specifically for weight loss
            // Discontinued but may still be mentioned
            "Belviq", "Lorcaserin",
            "Meridia", "Sibutramine",
            "Fen-phen", "Fenfluramine", "Dexfenfluramine",
            // OTC and supplements
            "Diet pills", "Weight loss pills", "Fat burner",
            "Garcinia", "Garcinia cambogia",
            "Green tea extract", "Green coffee bean",
            "Raspberry ketones", "Hydroxycut",
            "Ephedra", "Ephedrine", "Ma huang",
            // Diuretics sometimes misused for weight loss
            "Water pills", "Diuretics for weight loss",
            // Generic terms
            "Weight loss medication", "Diet medication",
            "Appetite suppressant", "Fat blocker"
          ],
          disqualifyIfCurrent: true,
          disqualifyWithin: "6 months",
        },
        {
          category: "corticosteroids",
          medications: [
            // Oral corticosteroids
            "Prednisone", "Deltasone", "Rayos", "Prednisone Intensol",
            "Prednisolone", "Prelone", "Orapred", "Orapred ODT", "Pediapred",
            "Methylprednisolone", "Medrol", "Medrol Dosepak", "Solu-Medrol",
            "Dexamethasone", "Decadron", "DexPak", "Hexadrol", "Dexasone",
            "Hydrocortisone", "Cortef", "Solu-Cortef", "Hydrocortone",
            "Betamethasone", "Celestone", "Celestone Soluspan",
            "Triamcinolone", "Kenalog", "Aristocort",
            "Cortisone", "Cortone",
            "Fludrocortisone", "Florinef",
            "Budesonide", "Entocort", "Uceris", // Oral forms only
            // Injectable long-acting
            "Depo-Medrol", "Kenalog injection", "Celestone injection",
            // Generic terms
            "Steroid", "Oral steroid", "Systemic steroid", "Corticosteroid",
            "Steroid pills", "Steroid injection", "Chronic steroids"
          ],
          excludes: [
            "Topical", "Cream", "Ointment", "Lotion", "Gel",
            "Inhaled", "Inhaler", "Pulmicort", "Symbicort", "Advair",
            "Nasal", "Flonase", "Nasonex", "Rhinocort",
            "Eye drops", "Ophthalmic", "Pred Forte",
            "Ear drops", "Otic",
            "Hemorrhoid", "Suppository",
            "Short course", "Dose pack", "Medrol dose pack", "Z-pack",
            "One time", "Single dose", "Emergency"
          ],
          disqualifyIfCurrent: true,
          disqualifyWithin: "3 months",
        },
        {
          category: "investigational",
          medications: [
            "Experimental", "Investigational", "Clinical trial",
            "Study drug", "Study medication", "Research drug",
            "Trial medication", "Experimental drug", "Experimental medication",
            "Investigational product", "Investigational drug",
            "Placebo", "Blinded medication", "Double blind",
            "Phase 1", "Phase 2", "Phase 3", "Phase 4",
            "Not FDA approved", "Off label", "Compassionate use"
          ],
          disqualifyIfCurrent: true,
          disqualifyWithin: "8 weeks", // or 5 half-lives
        },
      ],
    },
    disqualifyMessage: "This study has specific medication requirements. We cannot include participants taking certain medications.",
    // This will use the new AI medication mapper
    attributeMapping: mappers.aiMedication({
      // Maps AI results to same attributes as original questions
      categoryAttributeMap: {
        glp1: {
          semaglutide: GLP1_MEDICATIONS.SEMAGLUTIDE,
          tirzepatide: GLP1_MEDICATIONS.TIRZEPATIDE,
          liraglutide: GLP1_MEDICATIONS.LIRAGLUTIDE,
          dulaglutide: GLP1_MEDICATIONS.DULAGLUTIDE,
          exenatide: GLP1_MEDICATIONS.EXENATIDE,
        },
        dpp4_inhibitors: {
          sitagliptin: DPP4_MEDICATIONS.SITAGLIPTIN,
          alogliptin: DPP4_MEDICATIONS.ALOGLIPTIN,
          saxagliptin: DPP4_MEDICATIONS.SAXAGLIPTIN,
          linagliptin: DPP4_MEDICATIONS.LINAGLIPTIN,
        },
        insulin: {
          all: 23574, // has_insulin_daily
        },
        stimulants: {
          amphetamine: 22921, // amphetamine (generic for Adderall)
          lisdexamfetamine: 21522, // lisdexamfetamine
          methylphenidate: 21450, // methylphenidate
        },
        weight_loss: {
          phentermine: 21509, // phentermine
          orlistat: 21508, // orlistat
          naltrexone_bupropion: 23393, // naltrexone_bupropion
        },
        corticosteroids: {
          prednisone: IMMUNOSUPPRESSANT_MEDICATIONS.PREDNISONE,
          methylprednisolone: 21150, // methylprednisolone
          dexamethasone: 21151, // dexamethasone
          hydrocortisone: 21719, // hydrocortisone (oral/systemic)
        },
      },
    }),
    // Copy grid layout for fallback compatibility (from glp1History)
    gridLayout: vikingT2dScreeningQuestions.find(q => q.id === "glp1History")?.gridLayout,
  },
  
  // Skip insulinUse as it's covered by AI medication entry
  
  // Keep remaining non-medication questions
  vikingT2dScreeningQuestions.find(q => q.id === "weightChange")!,
  vikingT2dScreeningQuestions.find(q => q.id === "comorbidExclusions1")!,
  vikingT2dScreeningQuestions.find(q => q.id === "comorbidExclusions2")!,
  vikingT2dScreeningQuestions.find(q => q.id === "visitCommitment")!,
  vikingT2dScreeningQuestions.find(q => q.id === "currentTrial")!,
];